import { z } from "zod";

import { TASK_STATUSES, type TaskStatus } from "../tasks-api";

/**
 * Statuses eligible for the blocking step's fail-run policy. Terminal
 * statuses are excluded: `done` completes the step and `canceled` already
 * fails the run via the completion-event path.
 */
export const CREATE_TASK_FAILABLE_STATUSES = TASK_STATUSES.filter(
  (status): status is Exclude<TaskStatus, "done" | "canceled"> =>
    status !== "done" && status !== "canceled",
);

export const CREATE_TASK_DEFAULT_FAIL_ON_STATUSES: readonly TaskStatus[] = ["blocked"];

export type CreateTaskFailPolicy = {
  /** Statuses that fail the run while the step is blocked on the task. */
  failOnStatuses: TaskStatus[];
  /**
   * When true, a run failed by this policy is restored to `suspended`
   * (still waiting on the task) if the task later leaves the failing
   * status without completing.
   */
  continueOnNonFailingStatus: boolean;
};

/**
 * Resolve the fail-run policy from a create_task node config (or the
 * suspend state the worker stamps from it, which uses the same keys).
 * Absent `failOnStatuses` defaults to `["blocked"]`; terminal or unknown
 * entries are dropped.
 */
export function resolveCreateTaskFailPolicy(
  config: Record<string, unknown> | null | undefined,
): CreateTaskFailPolicy {
  const raw = config?.failOnStatuses;
  const failOnStatuses = Array.isArray(raw)
    ? raw.filter((value): value is TaskStatus =>
        (CREATE_TASK_FAILABLE_STATUSES as readonly string[]).includes(value as string),
      )
    : [...CREATE_TASK_DEFAULT_FAIL_ON_STATUSES];
  return {
    failOnStatuses,
    continueOnNonFailingStatus: config?.continueOnNonFailingStatus === true,
  };
}

/**
 * Contract for the `create_task` action node.
 *
 * Config validation for create_task is intentionally NOT tightened here —
 * the node has historically relied on the `requiredFields: []` fallback in
 * `ACTION_CONFIG_REQUIREMENTS`, and several optional fields (title,
 * description, status, priority, assignee, primaryResource,
 * additionalResources, awaitCompletion, …) are all valid. A permissive
 * passthrough keeps every existing node valid; the reason create_task
 * earns a contract at all is the **outputSchema**, which makes its outputs
 * referenceable in the editor's template picker and validated by the
 * edit-time dataflow checker (see node-contracts/dataflow.ts). Without a
 * contract, `{{ steps.<task>.output.* }}` references are invisible in the
 * picker and silently accepted (then fail at runtime).
 *
 * The fail-run policy fields ARE typed: they're new, so no legacy node can
 * carry a malformed value, and validating them at publish time keeps the
 * worker's resolver trivial.
 */
export const createTaskInputSchema = z.object({}).passthrough();

export const createTaskConfigSchema = z
  .object({
    failOnStatuses: z
      .array(z.enum(CREATE_TASK_FAILABLE_STATUSES as [TaskStatus, ...TaskStatus[]]))
      .optional(),
    continueOnNonFailingStatus: z.boolean().optional(),
    inheritInputFiles: z.boolean().optional(),
  })
  .passthrough();

export const createTaskOutputSchema = z.object({
  /** Public id of the created task (e.g. `tsk_…`). Always present. */
  taskPublicId: z.string(),
  /**
   * Absolute URL to the task in the web app, e.g.
   * `https://app.example.com/tasks/tsk_…`. Null when the worker has no
   * configured web origin (`CORS_ORIGIN` / `APP_URL`).
   */
  taskUrl: z.string().nullable(),
  /**
   * Public ids of files the agent generated while completing the task
   * (`entity_files.source_type = 'agent'`). Only populated on the blocking
   * (`awaitCompletion: true`) path once the task is done; otherwise empty.
   */
  generatedFileIds: z.array(z.string()),
  /** Terminal status on the blocking path: "done" | "canceled". */
  status: z.string().nullable(),
  /** Freeform outcome summary the assignee provided. */
  outcomeSummary: z.string().nullable(),
  /** Structured answers when the task carried questions. */
  answersJson: z.unknown(),
  /** ISO timestamp of completion (blocking path). */
  completedAt: z.string().nullable(),
  /** Public id of the actor who completed the task (blocking path). */
  completedBy: z.string().nullable(),
});

export type CreateTaskOutput = z.infer<typeof createTaskOutputSchema>;
